import type { ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { UpgradePrompt } from '@/features/billing/components/UpgradePrompt'
import { useAuthStore, useSubscription } from '@/stores/authStore'
import { useCan } from '@/hooks/useCan'

/**
 * Requires a resolved session.
 *
 * Checks the SESSION, not just the token: a token that failed to resolve is
 * cleared by AuthProvider, and gating on the token alone would render the
 * shell for half a second before bouncing.
 */
export function ProtectedRoute() {
  const token = useAuthStore((s) => s.token)
  const session = useAuthStore((s) => s.session)
  const location = useLocation()

  if (!token || !session) {
    // Preserve the destination so signing in returns them where they meant
    // to go, rather than to the dashboard.
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?next=${next}`} replace />
  }

  return (
    <>
      <Outlet />

      {/*
        Every 402, on every authenticated screen, including the POS.

        Here rather than in AppShell because the POS is deliberately outside
        AppShell — and `max_sales_per_month` is the limit a busy shop meets
        first, at the till. It renders nothing until the axios layer catches a
        plan limit, so it costs an idle store read.
      */}
      <UpgradePrompt />
    </>
  )
}

/**
 * A workspace that has not finished setup goes to the wizard.
 *
 * Sits INSIDE ProtectedRoute, so it only ever sees a resolved session.
 * `tenant.onboarded` rides on /me for exactly this: deciding on the first
 * render, rather than painting the dashboard and yanking it away.
 *
 * The wizard itself and the sign-out path are excluded, or the redirect would
 * fight itself. Nothing else is: skipping is offered inside the wizard, on
 * every step, rather than by leaving the URL.
 */
export function OnboardingGate() {
  const session = useAuthStore((s) => s.session)
  const location = useLocation()

  /*
   * The wizard belongs to whoever configures the workspace.
   *
   * `onboarded` is a property of the TENANT, so without this every staff
   * member in an unfinished workspace was sent to the setup wizard — a
   * cashier signing in at the till got "Tell us about your business" and no
   * way past it, because the redirect fires on every route. The API now
   * refuses them (`cap:settings.manage`), so leaving this as it was would
   * trap them on a screen that 403s.
   *
   * They go to the app instead. A shop can start selling before its owner has
   * finished the questions — that is the whole reason the wizard is skippable.
   */
  const canSetUp = useCan('settings.manage')

  const onboarded = session?.tenant.onboarded ?? true

  if (!onboarded && canSetUp && !location.pathname.startsWith('/onboarding')) {
    return <Navigate to="/onboarding" replace />
  }

  if (!onboarded && !canSetUp && location.pathname.startsWith('/onboarding')) {
    return <Navigate to="/dashboard" replace />
  }

  // The mirror: somebody who has finished has no business back in the wizard.
  // Without this, a bookmarked /onboarding shows a completed flow with a
  // Continue button that saves over settings they have since changed.
  if (onboarded && location.pathname.startsWith('/onboarding')) {
    return <Navigate to="/dashboard" replace />
  }

  return <Outlet />
}

/**
 * Only allow `?next=` to point back into this app.
 *
 * It arrives from the URL bar, so it is attacker-controlled. `//evil.com` is a
 * protocol-relative URL that browsers treat as absolute — the leading-slash
 * check alone is not enough, which is the usual shape of an open redirect.
 */
function safeNext(raw: string | null): string | null {
  if (!raw) return null

  let path: string
  try {
    path = decodeURIComponent(raw)
  } catch {
    return null
  }

  if (!path.startsWith('/') || path.startsWith('//')) return null
  if (path.startsWith('/login') || path.startsWith('/register')) return null

  return path
}

/** Login, register and friends — pointless once signed in. */
export function PublicOnlyRoute() {
  const token = useAuthStore((s) => s.token)
  const session = useAuthStore((s) => s.session)
  const location = useLocation()

  if (token && session) {
    // Honour the destination ProtectedRoute and the 401 handler preserved.
    // Without this, every "sign in and come back" path silently ends at the
    // dashboard, which makes the `next` they both set pointless.
    const next = safeNext(new URLSearchParams(location.search).get('next'))
    return <Navigate to={next ?? '/dashboard'} replace />
  }

  return <Outlet />
}

/**
 * Blocks the app when the subscription has fully lapsed.
 *
 * Billing and export stay reachable — locking a business away from its own
 * sales history because a card expired is the fastest route to a chargeback
 * and a regulator complaint.
 */
export function SubscriptionGate() {
  const subscription = useSubscription()
  const location = useLocation()

  const alwaysAllowed =
    location.pathname.startsWith('/billing') || location.pathname.startsWith('/settings')

  if (subscription && !subscription.can_sell && !alwaysAllowed) {
    return <Navigate to="/billing" replace />
  }

  return <Outlet />
}

/** Route-level capability gate. Cosmetic — the server re-checks everything. */
export function CapabilityRoute({
  cap,
  children,
}: {
  cap: string | string[]
  children?: ReactNode
}) {
  const allowed = useCan(cap)

  if (!allowed) return <Navigate to="/403" replace />

  return <>{children ?? <Outlet />}</>
}
